import { AnalyticsEventNameType } from "@coeff/api";
import * as Sentry from "@sentry/react";
import { RefObject, useEffect, useRef } from "react";

import { useTrack } from "../../utils";

import {
  DashboardWindow,
  enforceReadableCardText,
  normalizeSlicerStateForRealm,
  RenderFunction,
} from "./dashboardHelpers";
import { CleanupFunction, SlicerState } from "./useDashboardRuntime";
import { addPerfBreadcrumb } from "./webDashboardPerf";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

interface UseBlockRenderParams {
  cardRef: RefObject<HTMLDivElement>;
  // HTML to reset the card to before each render, so that render functions which
  // replace cardElement.innerHTML (e.g. for "no data" states) don't permanently
  // destroy the DOM structure expected by createChart/createDataTable.
  initialInnerHtml: string;
  renderFunctionJs: string;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  fullBlockId: string;
  blockId: string;
  blockType: string;
  blockTitle: string;
  // BE-provided pre-render error attribution from `block.error_state` (e.g.
  // missing source data). When set, the error fallback shows this string
  // verbatim in place of the generic "Unable to display ${label}" title and
  // switches the action button to "Learn more".
  errorState?: string | null;
  // Theme signature (id + mode + background). Any change re-runs the render so Chart.js instances
  // are re-created with the current theme — chart.update() alone does not refresh already-resolved
  // grid/tick colors, so a light/dark flip would otherwise keep the previous mode's gridlines.
  themeSignature?: string;
  // Signature of the block's persisted user overrides (e.g. chart_overrides). A change
  // re-runs the render so createChart re-applies them — the render JS itself is unchanged,
  // so no other dep would trigger the re-render.
  overridesSignature?: string;
  // Companion to `errorState`: open dict of structured attribution details so
  // new keys can be added without a schema bump. Today it carries `tabs` (the
  // source tab names implicated by the BE error), rendered as a "Tab: x" /
  // "Tabs: x, y" hover tooltip on the message.
  errorStateDetails?: ErrorStateDetails | null;
  onRendered?: (blockId: string) => void;
  onRenderStart?: (blockId: string) => void;
  onRenderComplete?: (blockId: string) => void;
  // Wired to the "Fix this" button shown in the runtime-JS-error fallback
  // (i.e. when `errorState` is null/undefined). Triggers the auto-fix loop.
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  // Wired to the "Learn more" button shown in the BE-attributed fallback
  // (i.e. when `errorState` is truthy). Opens the chat sidebar with an
  // EXPLAIN-with-error message — no auto-fix loop. `errorStateDetails` carries
  // the BE-attributed structured attribution (e.g. implicated source tabs) so
  // the chat handler can include them in the backend message to the agent.
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

/**
 * Open dict shape for `block.error_state_details`. New keys can be added without
 * a schema bump; consumers should treat unknown keys defensively. Today the only
 * documented key is `tabs` (source tab names implicated by the failure).
 */
export type ErrorStateDetails = {
  tabs?: string[] | null;
  [key: string]: unknown;
};

const BLOCK_TYPE_LABELS: Record<string, string> = {
  chart: "chart",
  data_table: "table",
  metric_card: "metric card",
};

// Formats the `tabs` entry in `error_state_details` into a "Tab: x" / "Tabs: x, y"
// label for the hover tooltip. Returns undefined when there are no tabs to show so
// callers can omit the tooltip. Future details keys may grow their own formatters.
export function formatErrorStateDetailsTooltip(
  errorStateDetails?: ErrorStateDetails | null
): string | undefined {
  const tabs = errorStateDetails?.tabs;
  if (!tabs || tabs.length === 0) {
    return undefined;
  }
  const label = tabs.length === 1 ? "Tab" : "Tabs";
  return `${label}: ${tabs.join(", ")}`;
}

/**
 * Computes the title and which action button to show in the error fallback.
 *
 * Two branches:
 * - `errorState` truthy (BE-attributed structural failure, e.g. missing source
 *   data): show `errorState` verbatim as the body and surface
 *   `errorStateDetails` on hover via the `tooltip` field; show "Learn more"
 *   wired to `onExplainBlockInChat` (no auto-fix — the cause is structural).
 * - `errorState` null/undefined (runtime JS error): show the generic
 *   "Unable to display ${label}" title; show "Fix this" wired to
 *   `onFixBlock` so the user can kick off the auto-fix loop.
 *
 * Exported so sibling components (e.g. MetricCardBlock) can mirror this
 * branching when they build their own variant of the fallback.
 */
export function getFallbackContent({
  blockType,
  errorState,
  errorStateDetails,
}: {
  blockType: string;
  errorState?: string | null;
  errorStateDetails?: ErrorStateDetails | null;
}): { title: string; tooltip?: string; actionButton: "fix" | "explain" } {
  if (errorState) {
    return {
      title: errorState,
      tooltip: formatErrorStateDetailsTooltip(errorStateDetails),
      actionButton: "explain",
    };
  }
  const label = BLOCK_TYPE_LABELS[blockType] ?? blockType;
  return { title: `Unable to display ${label}`, actionButton: "fix" };
}

export function renderErrorFallback(
  container: HTMLElement,
  blockType: string,
  blockId: string,
  errorMessage: string,
  blockTitle?: string,
  onFixBlock?: (blockId: string, errorMessage: string) => void,
  errorState?: string | null,
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void,
  errorStateDetails?: ErrorStateDetails | null
) {
  const { title, tooltip, actionButton } = getFallbackContent({
    blockType,
    errorState,
    errorStateDetails,
  });
  container.innerHTML = "";
  const doc = container.ownerDocument;

  // Metric cards render with p-0 on the card; add padding so the error state
  // isn't flush against the edges. Chart/table cards already carry their own padding.
  const outer = doc.createElement("div");
  outer.className = "flex flex-col w-full h-full" + (blockType === "metric_card" ? " p-4" : "");
  outer.setAttribute("data-block-error", "true");

  // Keep the block header visible so the failed block stays identifiable.
  if (blockTitle) {
    const header = doc.createElement("div");
    header.className = "themed-card-title";
    header.textContent = blockTitle;
    outer.appendChild(header);
  }

  const center = doc.createElement("div");
  center.className = "flex flex-1 flex-col items-center justify-center gap-1 text-center min-h-0";

  // Icon and message share a single row so the warning sign sits beside the text.
  const row = doc.createElement("div");
  row.className = "flex items-center gap-2 text-card-foreground";

  const icon = doc.createElement("span");
  icon.className = "inline-flex shrink-0";
  icon.innerHTML =
    `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" ` +
    `fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">` +
    `<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>` +
    `<line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

  const msg = doc.createElement("span");
  msg.style.cssText = "font-weight: 700; font-size: 16px; line-height: 20px;";
  msg.textContent = title;
  if (tooltip) {
    // Native browser tooltips look unstyled and float over the action button;
    // build a small styled bubble positioned above the message instead.
    msg.style.cursor = "default";
    msg.style.position = "relative";
    let tooltipEl: HTMLDivElement | null = null;
    const showTooltip = () => {
      if (tooltipEl) {
        return;
      }
      tooltipEl = doc.createElement("div");
      tooltipEl.textContent = tooltip;
      tooltipEl.style.cssText =
        "position: absolute; bottom: calc(100% + 6px); left: 50%; " +
        "transform: translateX(-50%); background: rgba(17, 24, 39, 0.95); " +
        "color: #fff; padding: 4px 8px; border-radius: 6px; font-size: 12px; " +
        "font-weight: 500; line-height: 16px; white-space: normal; " +
        "max-width: 220px; pointer-events: none; z-index: 10; " +
        "box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); text-align: center;";
      const arrow = doc.createElement("div");
      arrow.style.cssText =
        "position: absolute; top: 100%; left: 50%; transform: translateX(-50%); " +
        "border: 4px solid transparent; border-top-color: rgba(17, 24, 39, 0.95);";
      tooltipEl.appendChild(arrow);
      msg.appendChild(tooltipEl);
    };
    const hideTooltip = () => {
      tooltipEl?.remove();
      tooltipEl = null;
    };
    msg.addEventListener("mouseenter", showTooltip);
    msg.addEventListener("mouseleave", hideTooltip);
  }

  row.appendChild(icon);
  row.appendChild(msg);
  center.appendChild(row);

  if (actionButton === "explain" && onExplainBlockInChat) {
    const btn = doc.createElement("button");
    btn.className =
      "text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline " +
      "cursor-pointer bg-transparent border-0 p-0";
    btn.textContent = "Learn more";
    btn.addEventListener("click", e => {
      e.stopPropagation();
      onExplainBlockInChat(blockId, errorMessage, errorStateDetails);
    });
    center.appendChild(btn);
  } else if (actionButton === "fix" && onFixBlock) {
    const btn = doc.createElement("button");
    btn.className =
      "text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline " +
      "cursor-pointer bg-transparent border-0 p-0";
    btn.textContent = "Fix this";
    btn.addEventListener("click", e => {
      e.stopPropagation();
      onFixBlock(blockId, errorMessage);
    });
    center.appendChild(btn);
  }

  outer.appendChild(center);
  container.appendChild(outer);
}

export const useBlockRender = ({
  cardRef,
  initialInnerHtml,
  renderFunctionJs,
  slicerState,
  targetWindow,
  fullBlockId,
  blockId,
  blockType,
  blockTitle,
  errorState,
  errorStateDetails,
  themeSignature,
  overridesSignature,
  onRendered,
  onRenderStart,
  onRenderComplete,
  onFixBlock,
  onExplainBlockInChat,
}: UseBlockRenderParams) => {
  const track = useTrack();
  const { dashboardId, dashboardName, spreadsheetId, slicerNames } = useWebDashboardViewerContext();
  const cleanupRef = useRef<CleanupFunction | null>(null);
  const hasCalledOnRenderedRef = useRef(false);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (!cardRef.current) {
      return;
    }

    let cancelled = false;

    const executeRender = async () => {
      // Notify on re-renders (slicer changes) so the parent can show a loading overlay.
      // Skip the initial mount — the skeleton already handles that state.
      if (!isFirstRenderRef.current) {
        onRenderStart?.(blockId);
      }
      isFirstRenderRef.current = false;

      // Marker used by screenshot capture logic: avoid capturing mid-render when the DOM has
      // been reset but Chart.js/AG Grid hasn't painted yet.
      if (cardRef.current) {
        cardRef.current.dataset.coeffRenderInflight = "1";
      }

      if (cleanupRef.current) {
        try {
          await cleanupRef.current();
        } catch (error) {
          targetWindow.console.error(`Cleanup error for ${fullBlockId}:`, error);
        }
        cleanupRef.current = null;
      }

      if (cancelled) {
        return;
      }

      // Reset the card's DOM to its original structure before each render.
      // Render functions that set cardElement.innerHTML for empty states destroy
      // the .chart-canvas / .data-table-container nodes. Without this reset,
      // subsequent renders fail silently and require a browser refresh.
      if (cardRef.current) {
        cardRef.current.innerHTML = initialInnerHtml;
      }

      // Pre-render short-circuit: if the BE has already attributed a failure
      // (e.g. missing source data) via `block.error_state`, skip executing
      // `renderFunctionJs` entirely and render the fallback DOM directly. This
      // ensures the BE-attributed message is displayed even when the render
      // function would not have thrown. Default `errorState === undefined`
      // preserves the legacy try/catch path unchanged.
      if (errorState) {
        if (cardRef.current && !cancelled) {
          renderErrorFallback(
            cardRef.current,
            blockType,
            blockId,
            errorState,
            blockTitle,
            onFixBlock,
            errorState,
            onExplainBlockInChat,
            errorStateDetails
          );
          cardRef.current.dataset.coeffLastRenderedTs = String(Date.now());
          cardRef.current.dataset.coeffRenderInflight = "0";
        }

        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }

        if (!cancelled) {
          onRenderComplete?.(blockId);
        }

        // Emit per-render completion (failure) for screenshot coordination,
        // mirroring the catch-path behavior.
        if (!cancelled) {
          try {
            const CustomEventCtor = (targetWindow as any).CustomEvent || CustomEvent;
            const ev = new CustomEventCtor("coeff:block_render_complete", {
              detail: { blockId, ok: false, error: errorState },
            });
            targetWindow.dispatchEvent(ev);
            targetWindow.document.dispatchEvent(ev);
          } catch (e) {
            // Ignore.
          }
        }

        return;
      }

      const renderStart = Date.now();
      try {
        const cleanedRenderFunctionJs = renderFunctionJs.trim().replace(/;\s*$/, "");
        // eslint-disable-next-line no-new-func
        const sourceUrl = `${encodeURIComponent(blockId)}__render_function_js`;
        const renderFn = new targetWindow.Function(
          `return (${cleanedRenderFunctionJs});\n//# sourceURL=${sourceUrl}\n`
        )() as RenderFunction;

        const runSQL = targetWindow.runSQL;
        if (!runSQL) {
          throw new Error("runSQL is not initialized");
        }
        const cleanup = await renderFn(
          cardRef.current!,
          normalizeSlicerStateForRealm(slicerState, targetWindow),
          runSQL
        );

        // Breadcrumb (not a span): at render time there's no active root transaction,
        // so a per-block span would become its own unsampled transaction rather than
        // nest under the load. The breadcrumb captures per-block timing in the replay.
        addPerfBreadcrumb("block_render", {
          dashboard_id: dashboardId,
          block_id: blockId,
          block_type: blockType,
          ms: Date.now() - renderStart,
          ok: true,
        });

        if (cleanup && !cancelled) {
          cleanupRef.current = cleanup;
        }

        if (cardRef.current && !cancelled) {
          cardRef.current.dataset.coeffLastRenderedTs = String(Date.now());
          cardRef.current.dataset.coeffRenderInflight = "0";

          // Hide description when the render function shows a no-data / empty
          // state to avoid cluttering the view. Detect by checking if any
          // text-gray-500 element was injected (standard pattern in generated
          // render functions) or if the chart canvas was removed.
          const descEl = cardRef.current.querySelector<HTMLElement>(".block-description");
          if (descEl) {
            const noDataInserted = cardRef.current.querySelector(
              ".text-gray-500, .text-gray-400, [data-no-data]"
            );
            descEl.style.display = noDataInserted ? "none" : "";
          }

          // Backward-compat: keep neutral text the block hardcoded (e.g. `text-gray-*`) readable
          // against the themed card surface. Best-effort — never fail the render over it.
          try {
            enforceReadableCardText(
              cardRef.current,
              targetWindow as unknown as Window & typeof globalThis
            );
          } catch (readabilityError) {
            targetWindow.console.warn(
              `Readable-text pass failed for ${fullBlockId}:`,
              readabilityError
            );
          }
        }

        // Fire once-per-mount callback (used for overall dashboard loaded tracking).
        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }

        // Signal every successful render completion (including slicer-driven re-renders)
        // so the parent can dismiss loading overlays.
        if (!cancelled) {
          onRenderComplete?.(blockId);
        }

        // Fire per-render completion event so the parent can coordinate block-level
        // screenshot capture after AI edits.
        if (!cancelled) {
          try {
            const CustomEventCtor = (targetWindow as any).CustomEvent || CustomEvent;
            const ev = new CustomEventCtor("coeff:block_render_complete", {
              detail: { blockId, ok: true },
            });
            // Prefer dispatching on window because the iframe document object is replaced
            // on srcDoc reloads, which can detach document-level listeners.
            targetWindow.dispatchEvent(ev);
            // Backward compatible: also dispatch on document for any legacy listeners.
            targetWindow.document.dispatchEvent(ev);
          } catch (e) {
            // Ignore event dispatch failures; rendering succeeded.
          }
        }
      } catch (error) {
        // Log to the iframe console so console capture can detect it.
        targetWindow.console.error(`Render error for ${fullBlockId}:`, error);
        const errorMessage = error instanceof Error ? error.message : String(error);

        addPerfBreadcrumb("block_render", {
          dashboard_id: dashboardId,
          block_id: blockId,
          block_type: blockType,
          ms: Date.now() - renderStart,
          ok: false,
          error: errorMessage,
        });

        if (cardRef.current && !cancelled) {
          cardRef.current.dataset.coeffLastRenderedTs = String(Date.now());
          cardRef.current.dataset.coeffRenderInflight = "0";
        }

        Sentry.captureException(error, {
          tags: { feature: "web_dashboard", errorType: "block_render" },
          extra: {
            dashboardId,
            blockId,
            blockType,
            blockTitle,
            dashboardName,
            spreadsheetId,
          },
        });

        track(AnalyticsEventNameType.VibeDashBlockRenderingFailed, {
          vibe_dash_id: dashboardId,
          vibe_dash_block_id: blockId,
          vibe_dash_block_type: blockType,
          vibe_dash_block_title: blockTitle,
          vibe_dash_slicers: slicerNames,
          error_message: errorMessage,
          ss_id: spreadsheetId,
          vibe_dash_name: dashboardName,
        });

        if (!cancelled && cardRef.current) {
          renderErrorFallback(
            cardRef.current,
            blockType,
            blockId,
            errorMessage,
            blockTitle,
            onFixBlock,
            errorState,
            onExplainBlockInChat,
            errorStateDetails
          );
        }

        // Still notify on failure so the load event isn't blocked by a bad block.
        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }

        if (!cancelled) {
          onRenderComplete?.(blockId);
        }

        // Also emit per-render completion (failure) for screenshot coordination.
        if (!cancelled) {
          try {
            const CustomEventCtor = (targetWindow as any).CustomEvent || CustomEvent;
            const ev = new CustomEventCtor("coeff:block_render_complete", {
              detail: { blockId, ok: false, error: errorMessage },
            });
            targetWindow.dispatchEvent(ev);
            targetWindow.document.dispatchEvent(ev);
          } catch (e) {
            // Ignore.
          }
        }
      }
    };

    executeRender();

    return () => {
      cancelled = true;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [
    fullBlockId,
    initialInnerHtml,
    renderFunctionJs,
    slicerState,
    targetWindow,
    themeSignature,
    overridesSignature,
    track,
    dashboardId,
    blockType,
    errorState,
  ]); // eslint-disable-line react-hooks/exhaustive-deps
};
